import clsx from 'clsx';
import { Formik } from 'formik';
import React, { useState } from 'react';
import { Form } from 'react-bootstrap';

import { useGiftSubscriptionCreate, useGroupSubscriptionConfirm } from '../../../../hooks';
import { GROUP_SUBSCRIPTION_GIFT } from '../../../../src/constants';
import { AutocompleteOption } from '../../../../utils/interfaces';
import { Button, Divider } from '../../../BaseUI';
import { RadioButtonGroup, TextAreaField, TokenizeTextField } from '../../../FormFields';
import schema from './schema';
import styles from './styles.module.scss';

const AUTO_RENEW_OPTIONS = [
  { label: 'Ongoing subscription', value: 'ongoing_subscription' },
  { label: 'One year only', value: 'one_year_only' },
];

export interface FormValues {
  emails: AutocompleteOption[];
  notes: string;
  autoRenew: string;
}

const INITIAL_STATE: FormValues = {
  emails: [],
  notes: '',
  autoRenew: AUTO_RENEW_OPTIONS[0].value,
};

const GiftForm = ({
  forGiftLandingPage,
  setCurrentFormStep,
  setNumberOfGifts,
}: {
  forGiftLandingPage?: boolean;
  setCurrentFormStep?: (state: number) => void;
  setNumberOfGifts?: (state: number) => void;
}) => {
  const { mutateAsync: createGiftSubscription, isLoading: isLoadingPurchase } =
    useGiftSubscriptionCreate(forGiftLandingPage);
  const { mutateAsync: confirmGroupSubscription } = useGroupSubscriptionConfirm();
  const [totalCost, setTotalCost] = useState<number>(0);
  const [totalEligibleSubs, setTotalEligibleSubs] = useState<number>(0);

  const handleSubmit = async ({ emails, notes, autoRenew }, formik) => {
    if (isLoadingPurchase) {
      return;
    }
    const sanitizedParams = {
      emails: emails.map((email: AutocompleteOption) => email.value),
      notes,
      auto_renew: autoRenew === 'ongoing_subscription',
    };
    await createGiftSubscription(sanitizedParams);
    formik.resetForm();
    if (forGiftLandingPage) {
      setCurrentFormStep(1);
      setNumberOfGifts(totalEligibleSubs);
    }
  };

  const prepareConfirmation = async ({ emails }: { emails: AutocompleteOption[] }) => {
    const giftData = await confirmGroupSubscription({
      emails: emails.map((email) => email.value),
      group_subscription_type: GROUP_SUBSCRIPTION_GIFT,
    });
    setTotalCost(giftData.totalPrice);
    setTotalEligibleSubs(giftData.totalEligibleSubs);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={INITIAL_STATE}
      onSubmit={handleSubmit}
      validationSchema={schema}
      validateOnBlur={false}
    >
      {(formik) => (
        <Form noValidate onSubmit={formik.handleSubmit} className={styles.formContainer}>
          <TokenizeTextField
            name="emails"
            label={forGiftLandingPage ? '' : 'Emails'}
            className={styles.tokenTxtField}
            onChangeCallBack={() => prepareConfirmation(formik.values)}
          />
          <span className={styles.tokenizeHint}>
            Add people by email separating each address by a comma.
          </span>
          <TextAreaField
            name="notes"
            autoComplete="off"
            minRows={6}
            placeholder="Add an optional note which will be sent to gift recipients with their login information."
          />
          <RadioButtonGroup name="autoRenew" options={AUTO_RENEW_OPTIONS} />
          {totalEligibleSubs > 0 && formik.isValid && formik.dirty && (
            <>
              <Divider marginY="medium" />
              <div className={styles.totalContainer}>
                <div>Accounts total: {totalEligibleSubs}</div>
                <div>${totalCost / 100}.00</div>
              </div>
              {formik.values.autoRenew === 'ongoing_subscription' && (
                <span className={styles.accountSummaryNote}>
                  After initial year subscription will renew at base subscription pricing
                </span>
              )}
              <Divider marginY="medium" />
            </>
          )}
          <Button
            variant="primary"
            className={clsx(styles.nextBtn, {
              [styles.purchaseBtnLanding]: forGiftLandingPage,
            })}
            type="submit"
            disabled={!(formik.isValid && formik.dirty) || isLoadingPurchase}
          >
            {isLoadingPurchase ? 'Processing' : 'Purchase'}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default GiftForm;
